import React, { useRef, useState } from "react";
import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import { Table, Popconfirm, Input, message } from "antd";
import {
  useContactList,
  useDeleteContactForm,
} from "../../../hooks/api/contact/hooks";
import { IContactApiResponse } from "../../../types/api/contact";
import { QData } from "../../../types/basic-type-list";
import MyPagination from "../../components/Pagination";
import { ColumnType } from "antd/es/table";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Select, Button } from "antd";

interface ContactListingProps {
  formType: "CONTACT" | "ENQUIRY";
  allowURLStateUpdate?: boolean;
}

const EnquiryListing: React.FC<ContactListingProps> = ({
  formType,
  allowURLStateUpdate = true,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [editing, setEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { data, refetchWithQuery, isFetching } = useContactList(formType);
  const contactList: IContactApiResponse[] = data?.data ?? [];
  const qData: Required<QData> = {
    page: data?.qData?.page ?? 1,
    limit: data?.qData?.limit ?? 20,
    total: data?.qData?.total ?? 0,
  };

  const DEFAULT_NUQS_CONFIG = {
    throttleMs: !allowURLStateUpdate ? Infinity : 0,
  };

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearch = (value: string) => {
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      refetchWithQuery({
        page: 1,
        limit: qData.limit,
        name: value,
      });
    }, 500);
  };
  const deleteMutation = useDeleteContactForm();
  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      messageApi.success("Data Deleted successfully");
    } catch (err: any) {
      messageApi.error(err.message || "Delete failed");
    }
  };

  const handleExport = () => {
    const formattedData = contactList.map(
      (item: IContactApiResponse, index: number) => ({
        "S.No": (qData.page - 1) * qData.limit + index + 1,
        "Full Name": item?.enquiry?.name || "-",
        "Function": item?.enquiry?.function || "-",
        "Industry": item?.enquiry?.industry || "-",
        "Message": item?.enquiry?.message || "-",
        "Resume": item?.enquiry?.resume
          ? {
            t: "s",
            v: "View Resume",
            l: { Target: item.enquiry.resume },
          }
          : "-",
        "Date": item?.createdAt
          ? moment(item.createdAt).format("DD MMM YYYY, hh:mm A")
          : "-",
      })
    );

    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    worksheet["!cols"] = [
      { wch: 6 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 40 },
      { wch: 25 },
      { wch: 22 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Enquiries");

    XLSX.writeFile(workbook, `enquiries_page_${qData.page}.xlsx`);
  };

  const columns: ColumnType<IContactApiResponse>[] = [
    {
      title: "SN",
      key: "sn",
      width: 70,
      render: (_: any, __: any, index: number) => <span>{index + 1}</span>,
    },
    {
      title: "Full Name",
      key: "name",
      render: (record: IContactApiResponse) =>
        formType === "ENQUIRY" ? `${record.enquiry?.name ?? ""}` : "-",
    },
    {
      title: "Function",
      key: "function",
      render: (record: IContactApiResponse) =>
        formType === "ENQUIRY" ? record.enquiry?.function : "-",
    },
    {
      title: "Industry",
      key: "industry",
      render: (record: IContactApiResponse) =>
        formType === "ENQUIRY" ? record.enquiry?.industry : "-",
    },
    {
      title: "Message",
      key: "message",
      render: (record: IContactApiResponse) =>
        formType === "ENQUIRY" ? record.enquiry?.message : "-",
    },
    {
      title: "Resume",
      key: "resume",
      render: (record: IContactApiResponse) =>
        formType === "ENQUIRY" ? (
          <a
            href={record.enquiry?.resume}
            target="_blank"
            className="text-[10px] sm:text-xs underline text-primary"
          >
            View Resume
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Date",
      key: "createdAt",
      render: (record: IContactApiResponse) =>
        record?.createdAt
          ? moment(record?.createdAt).format("DD MMM YYYY, hh:mm A")
          : "-",
    },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (record: IContactApiResponse) => (
        <Popconfirm
          title="Are you sure to delete this record?"
          onConfirm={() => handleDelete(record._id)}
          okText="Yes"
          cancelText="No"
        >
          <DeleteOutlined className="cursor-pointer text-red-600 text-[18px]" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}

      {formType === "ENQUIRY" && (
        <div className="mb-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <Input
            placeholder="Search by name..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            className="sm:w-[300px]"
          />

          <div className="flex gap-2">
            <Button type="primary" onClick={handleExport}>
              Export To Excel
            </Button>
          </div>
        </div>
      )}

      <div className="hidden sm:block">
        <Table
          dataSource={contactList}
          columns={columns}
          rowKey={(item) => item._id}
          pagination={false}
          size="middle"
          loading={isFetching}
          scroll={{ x: true }}
          className="custom-table-class rounded-lg border border-gray-200"
          footer={() => (
            <MyPagination
              qData={qData}
              onChange={refetchWithQuery}
              total={qData.total}
              DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
            />
          )}
        />
      </div>

      <div className="sm:hidden space-y-4 mt-4">
        {contactList.length > 0 ? (
          contactList.map((item) => (
            <div
              key={item._id}
              className="bg-[#F9F9F9] border border-gray-200 rounded-lg p-4 shadow-sm space-y-2"
            >
              <div>
                <b>Full Name:</b> {item?.enquiry?.name}
              </div>

              <div>
                <b>Function:</b> {item?.enquiry?.function ? item?.enquiry?.function : "-"}
              </div>

              <div>
                <b>Industry:</b> {item?.enquiry?.industry ? item?.enquiry?.industry : "-"}
              </div>

              <div>
                <b>Message:</b> {item?.enquiry?.message ?? "-"}
              </div>
              <div>
                <b>Resume:</b>{" "}
                {item?.enquiry?.resume ? (
                  <a
                    href={item?.enquiry?.resume}
                    target="_blank"
                    className="text-primary underline"
                  >
                    View Resume
                  </a>
                ) : (
                  "-"
                )}
              </div>
              <div>
                <b>Date:</b>{" "}
                {moment(item?.createdAt).format("DD MMM YYYY, hh:mm A")}
              </div>

              <div className="flex justify-end gap-4 text-primary text-xl mt-3">
                <Popconfirm
                  title="Are you sure to delete this record?"
                  onConfirm={() => item?._id && handleDelete(item._id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <DeleteOutlined className="cursor-pointer text-red-500" />
                </Popconfirm>
              </div>
            </div>
          ))
        ) : (
          <p className="text-center py-6 text-gray-500">No leads found</p>
        )}
        <MyPagination
          qData={qData}
          onChange={refetchWithQuery}
          total={qData.total}
          DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
        />
      </div>
    </div>
  );
};

export default EnquiryListing;
